import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Ban, Clock3, KeyRound, RefreshCw, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EMPTY_FORM = {
  permission: "",
  effect: "allow",
  mode: "temporary",
  duration_minutes: 120,
  case_reference: "",
  reason: "",
};

const roleLabel = (roles = []) => {
  const preferred = ["admin", "support", "consultant", "creator", "student", "user"];
  return preferred.find((role) => roles.includes(role)) || "user";
};

const grantStatusLabel = {
  active: "Activo",
  scheduled: "Programado",
  expired: "Vencido",
  revoked: "Revocado",
};

const PermissionGovernance = () => {
  const [users, setUsers] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [catalog, setCatalog] = useState({ delegable: [], director_exclusive: [] });
  const [grants, setGrants] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const loadGrants = useCallback(async (userId) => {
    if (!userId) {
      setGrants([]);
      return;
    }
    const { data } = await api.get(`/api/director/users/${userId}/access-grants`);
    setGrants(data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [usersResponse, catalogResponse] = await Promise.all([
        api.get("/api/admin/users", { params: { limit: 500 } }),
        api.get("/api/director/access-grants/catalog"),
      ]);
      const assignable = usersResponse.data.filter(
        (user) => !(user.roles || []).includes("director"),
      );
      setUsers(assignable);
      setCatalog(catalogResponse.data);
      const selected = assignable.some((user) => user.id === targetId)
        ? targetId
        : (assignable[0]?.id || "");
      setTargetId(selected);
      setForm((current) => ({
        ...current,
        permission: current.permission || catalogResponse.data.delegable?.[0]?.key || "",
      }));
      await loadGrants(selected);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [loadGrants, targetId]);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!targetId) return;
    setError("");
    loadGrants(targetId).catch((requestError) => setError(formatApiError(requestError)));
  }, [loadGrants, targetId]);

  const groupedCatalog = useMemo(() => {
    const groups = new Map();
    (catalog.delegable || []).forEach((item) => {
      if (!groups.has(item.group)) groups.set(item.group, []);
      groups.get(item.group).push(item);
    });
    return [...groups.entries()];
  }, [catalog.delegable]);

  const submit = async (event) => {
    event.preventDefault();
    if (!targetId) return;
    setBusy("grant");
    setError("");
    setOk("");
    try {
      const payload = {
        ...form,
        duration_minutes: form.mode === "temporary" ? Number(form.duration_minutes) : null,
      };
      await api.post(`/api/director/users/${targetId}/access-grants`, payload);
      setOk(form.effect === "allow" ? "Permiso individual concedido." : "Restricción individual aplicada.");
      setForm((current) => ({ ...EMPTY_FORM, permission: current.permission }));
      await loadGrants(targetId);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  const revoke = async (grant) => {
    const reason = window.prompt("Motivo obligatorio de la revocación:");
    if (!reason?.trim()) return;
    setBusy(grant.id);
    setError("");
    setOk("");
    try {
      await api.post(
        `/api/director/users/${targetId}/access-grants/${grant.id}/revoke`,
        { reason: reason.trim() },
      );
      setOk("Regla revocada inmediatamente.");
      await loadGrants(targetId);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  const selectedUser = users.find((user) => user.id === targetId);

  return (
    <section className="mt-8" data-testid="permission-governance">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="kd9-gold-text" />
            <h2 className="font-cinzel kd9-gold-text text-xl">GOBIERNO INDIVIDUAL DE PERMISOS</h2>
          </div>
          <p className="font-cormorant mt-1" style={{ color: "var(--kd9-text-soft)" }}>
            La base del rol permanece estable. Dirección puede ampliar o restringir una cuenta,
            de forma permanente o temporal, con caso, motivo, vencimiento y auditoría.
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="kd9-btn-outline p-2 rounded-lg" aria-label="Actualizar permisos">
          <RefreshCw size={15} />
        </button>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg border border-red-800 text-red-300" role="alert">{error}</div>}
      {ok && <div className="mb-4 p-3 rounded-lg border border-emerald-700 text-emerald-300">{ok}</div>}

      <div className="kd9-card p-5 mb-5">
        <label className="text-xs">Persona o cuenta
          <select className="kd9-input w-full mt-1 p-3" value={targetId} onChange={(event) => setTargetId(event.target.value)}>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name || user.email} · {roleLabel(user.roles)} · {user.email}
              </option>
            ))}
          </select>
        </label>
        {selectedUser && (
          <div className="mt-3 text-[11px]" style={{ color: "var(--kd9-text-soft)" }}>
            Base actual: {(selectedUser.roles || []).join(", ")}. Los permisos exclusivos del Director nunca aparecen como concedibles.
          </div>
        )}
      </div>

      <form onSubmit={submit} className="kd9-card p-5 mb-5">
        <div className="grid md:grid-cols-2 gap-4">
          <label className="text-xs">Permiso
            <select className="kd9-input w-full mt-1 p-3" required value={form.permission} onChange={(event) => setForm({ ...form, permission: event.target.value })}>
              {groupedCatalog.map(([group, permissions]) => (
                <optgroup key={group} label={group}>
                  {permissions.map((item) => <option key={item.key} value={item.key}>{item.key}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="text-xs">Decisión
            <select className="kd9-input w-full mt-1 p-3" value={form.effect} onChange={(event) => setForm({ ...form, effect: event.target.value })}>
              <option value="allow">Conceder capacidad extra</option>
              <option value="deny">Restringir capacidad existente</option>
            </select>
          </label>
          <label className="text-xs">Vigencia
            <select className="kd9-input w-full mt-1 p-3" value={form.mode} onChange={(event) => setForm({ ...form, mode: event.target.value })}>
              <option value="temporary">Temporal por caso</option>
              <option value="permanent">Permanente hasta revocación</option>
            </select>
          </label>
          {form.mode === "temporary" && (
            <label className="text-xs">Duración en minutos
              <input className="kd9-input w-full mt-1 p-3" type="number" min={15} max={10080} required value={form.duration_minutes} onChange={(event) => setForm({ ...form, duration_minutes: event.target.value })} />
            </label>
          )}
          <label className="text-xs">Caso o incidencia
            <input className="kd9-input w-full mt-1 p-3" required minLength={3} maxLength={120} placeholder="Ej. INC-2026-091" value={form.case_reference} onChange={(event) => setForm({ ...form, case_reference: event.target.value })} />
          </label>
          <label className="text-xs md:col-span-2">Motivo y alcance
            <textarea className="kd9-input w-full mt-1 p-3" rows={3} required minLength={10} maxLength={1000} value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button disabled={busy === "grant" || !targetId || !form.permission} className="kd9-btn-primary px-6 py-3 rounded-lg">
            <KeyRound size={14} className="inline mr-2" />
            {busy === "grant" ? "Aplicando…" : "Aplicar regla individual"}
          </button>
          <span className="text-[10px] uppercase tracking-widest opacity-60">
            {catalog.director_exclusive?.length || 0} capacidades críticas protegidas
          </span>
        </div>
      </form>

      <div className="kd9-card overflow-hidden">
        <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "var(--kd9-border)" }}>
          <Clock3 size={16} className="kd9-gold-text" />
          <strong>Historial y reglas vigentes</strong>
        </div>
        {grants.length === 0 ? (
          <div className="p-8 text-center" style={{ color: "var(--kd9-text-soft)" }}>Esta cuenta conserva únicamente su matriz base.</div>
        ) : grants.map((grant) => (
          <div key={grant.id} className="p-4 border-b last:border-0 grid lg:grid-cols-[1fr_170px_150px_auto] gap-3 items-center" style={{ borderColor: "var(--kd9-border)" }}>
            <div className="min-w-0">
              <div className="font-mont text-sm break-all">{grant.permission}</div>
              <div className="text-[11px]" style={{ color: "var(--kd9-text-soft)" }}>
                {grant.effect === "allow" ? "Concedido" : "Restringido"} · {grant.case_reference} · {grant.reason}
              </div>
              <div className="text-[10px] mt-1 opacity-60">
                Usos auditados: {grant.usage_count || 0}
                {grant.last_used_at ? ` · Último: ${new Date(grant.last_used_at).toLocaleString("es-CO")}` : ""}
              </div>
            </div>
            <div className="text-xs">
              {grant.mode === "temporary" ? (
                <>Vence: {grant.expires_at ? new Date(grant.expires_at).toLocaleString("es-CO") : "—"}</>
              ) : "Permanente"}
            </div>
            <span className="text-[10px] uppercase tracking-widest">{grantStatusLabel[grant.status] || grant.status}</span>
            {["active", "scheduled"].includes(grant.status) ? (
              <button type="button" disabled={busy === grant.id} onClick={() => revoke(grant)} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px]">
                <Ban size={13} className="inline mr-1" /> Revocar
              </button>
            ) : <span className="text-[10px] opacity-50">Cerrado</span>}
          </div>
        ))}
      </div>
    </section>
  );
};

export default PermissionGovernance;

